// @flow
import { TridentResourceName, TridentScopeNames } from 'data/trident-campaigns';
import jwtDecode from 'jwt-decode';
import _ from 'lodash';
import moment from 'moment';
import { getCookie, removeCookie, setCookie } from 'omega/utils/cookie-utils';
import { getAllowedTargetUserTypesList as legacyGetAllowedTargetUserTypesList } from 'utils/new-trident-utils';

export function hasPermission(permissions: Object, resource: string, field: string, accessType: string): boolean {
  const val = _.get(permissions, ['authorizations', resource, field, accessType], false);
  return val;
}

// getValueByFeatureFlag is a helper function to get the value from the feature flag
export function getValueByFeatureFlag(isEnabled: Boolean, primaryValue, fallbackValue) {
  if (isEnabled) {
    return primaryValue;
  }
  return fallbackValue;
}

// hasPermission is the permission checking function specific for Trident
export function hasTridentPermission(
  permissions: Object,
  userType: string,
  owner: string,
  action: string,
): boolean {
  if (_.isEmpty(permissions)) {
    return false;
  }
  const { authorizations, userScopes } = permissions;

  if (userType === 'moveit_passenger') {
    userType = 'passenger';
  } else if (userType === 'moveit_driver') {
    userType = 'driver';
  }

  // if permission not found in the legacy method, try finding permission in the scope
  // first, confirm whether user has permission for the action
  const tridentPermissions = _.get(authorizations, TridentResourceName, {}); // in the form of {"fieldName": {"read": true/false, "create": ...}, ...}
  if (!_.some(tridentPermissions, (p) => _.get(p, action) === true)) {
    return false;
  }

  // second, check whether user has access to the userType and owner
  let userTypeAllowed = false;
  _.map(_.get(userScopes, TridentScopeNames.userType, []), (u) => {
    if (_.toLower(u) === _.toLower(userType)) {
      userTypeAllowed = true;
    }
  });
  if (!_.isEmpty(userType) && !userTypeAllowed) {
    return false;
  }

  let ownerAllowed = false;
  _.map(_.get(userScopes, TridentScopeNames.owner, []), (o) => {
    if (_.toLower(o) === _.toLower(owner)) {
      ownerAllowed = true;
    }
  });

  return _.isEmpty(owner) || ownerAllowed;
}

// getAllowedTargetUserTypesList takes in a userTypeList, and filter out the list of user types that are allowed according to the permissions
export function getAllowedTargetUserTypesList(
  permissions: Object,
  userTypeList: Array<Object>,
  actionType: string,
) {
  if (_.isEmpty(_.get(permissions, 'userScopes'))) {
    // this user is using the old roles
    return legacyGetAllowedTargetUserTypesList(
      permissions,
      userTypeList,
      actionType,
    );
  }

  return userTypeList.filter(({ value }) => {
    return hasTridentPermission(permissions, value, '', actionType);
  });
}

export const ACCESS_TOKEN_COOKIE = 'omega-auth-access';
export const REFRESH_TOKEN_COOKIE = 'omega-auth-refresh';
export const AUTH_TYPE_COOKIE = 'omega-auth-type';
export const BANNER_COOKIE = 'omega-banner';

export const defaultCookieOptions = (host = window.location.host) => ({
  domain: host.replace(/omega[a-z-]*/, ''),
  secure: true,
  path: '/',
});

export const saveAuthCookie = (accessToken, refreshToken, authType = 'concedo') => {
  const jwtData = jwtDecode(accessToken);
  const cookieOptions = {
    // expire takes a date object with epoch milliseconds
    expires: new Date(jwtData.exp * 1000),
    ...defaultCookieOptions(),
  };

  setCookie(ACCESS_TOKEN_COOKIE, accessToken, cookieOptions);
  setCookie(REFRESH_TOKEN_COOKIE, refreshToken, cookieOptions);
  setCookie(AUTH_TYPE_COOKIE, authType, cookieOptions);
};

export const removeAuthCookie = () => {
  removeCookie(ACCESS_TOKEN_COOKIE, defaultCookieOptions());
  removeCookie(REFRESH_TOKEN_COOKIE, defaultCookieOptions());
  removeCookie(AUTH_TYPE_COOKIE, defaultCookieOptions());
};

export const getAuthToken = () => {
  const accessToken = getCookie(ACCESS_TOKEN_COOKIE);
  const refreshToken = getCookie(REFRESH_TOKEN_COOKIE);
  const authType = getCookie(AUTH_TYPE_COOKIE);
  return { accessToken, refreshToken, authType };
};

export const setBannerCookie = message => {
  const cookieOptions = {
    ...defaultCookieOptions(),
    expires: moment().endOf('day').toDate(),
  };
  setCookie(BANNER_COOKIE, message, cookieOptions);
};

export const getBannerCookie = () => {
  return getCookie(BANNER_COOKIE);
};
